import React, { useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonSizes, ButtonThemes } from "ui-components/button/button";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { useDockScanMutations, useDockTags } from "../hooks/useDockScanQueries";
import DockEmployeeTagSelect, {
  resolveTagIdsFromSelections,
} from "./DockEmployeeTagSelect";

interface CreateDockEmployeeSidebarProps {
  show: boolean;
  terminalNumber: number;
  onClose: () => void;
  onCreated: (employeeId: string) => void;
}

const createSchema = Yup.object({
  employeeId: Yup.string().trim().required("Employee ID is required").max(32),
  email: Yup.string().email("Invalid email").required("Email is required"),
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
});

const CreateDockEmployeeSidebar: React.FC<CreateDockEmployeeSidebarProps> = ({
  show,
  terminalNumber,
  onClose,
  onCreated,
}) => {
  const { data: tags } = useDockTags(terminalNumber);
  const { createEmployee, createTag } = useDockScanMutations(terminalNumber);
  const [error, setError] = useState<string | null>(null);

  return (
    <SlideOutSidebar
      show={show}
      onClose={onClose}
      header="Add Dock User"
      size="small"
    >
      <p className="text-light dock-users__create-hint">
        Employee ID is scanned on the gun barcode and cannot be changed after creation.
      </p>
      <Formik
        initialValues={{
          employeeId: "",
          email: "",
          firstName: "",
          lastName: "",
          tags: [] as XGSSelectOption[],
        }}
        validationSchema={createSchema}
        onSubmit={async (values, { resetForm }) => {
          setError(null);
          try {
            const tagIds = await resolveTagIdsFromSelections(
              values.tags,
              tags || [],
              (name) => createTag.mutateAsync({ terminalNumber, name })
            );
            const created = await createEmployee.mutateAsync({
              employeeId: values.employeeId.trim(),
              email: values.email.trim(),
              firstName: values.firstName.trim(),
              lastName: values.lastName.trim(),
              terminalNumber,
              tagIds: tagIds.length ? tagIds : undefined,
            });
            resetForm();
            onCreated(created.employeeId);
            onClose();
          } catch (e) {
            setError((e as Error).message);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, dirty, isSubmitting, setFieldValue }) => (
          <Form className="dock-users__sidebar-form">
            <LabeledTextInput
              name="employeeId"
              labelMode={LabelModes.column}
              label="Employee ID"
              value={values.employeeId}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g. 600001"
              error={touched.employeeId && errors.employeeId ? errors.employeeId : undefined}
            />
            <LabeledTextInput
              name="firstName"
              labelMode={LabelModes.column}
              label="First Name"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.firstName && errors.firstName ? errors.firstName : undefined}
            />
            <LabeledTextInput
              name="lastName"
              labelMode={LabelModes.column}
              label="Last Name"
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.lastName && errors.lastName ? errors.lastName : undefined}
            />
            <LabeledTextInput
              name="email"
              labelMode={LabelModes.column}
              label="Email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email ? errors.email : undefined}
            />
            <div className="dock-users__tag-picker">
              <DockEmployeeTagSelect
                tags={tags || []}
                value={values.tags}
                onChange={(options) => setFieldValue("tags", options)}
                disabled={isSubmitting || createEmployee.isLoading || createTag.isLoading}
              />
            </div>

            {error && <XGSErrorMessage>{error}</XGSErrorMessage>}

            <div className="dock-users__sidebar-actions">
              <Button
                type="submit"
                theme={ButtonThemes.blue}
                size={ButtonSizes.medium}
                disabled={!dirty || isSubmitting || createEmployee.isLoading || createTag.isLoading}
              >
                Create
              </Button>
              <Button type="button" theme={ButtonThemes.gray} size={ButtonSizes.medium} onClick={onClose}>
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </SlideOutSidebar>
  );
};

export default CreateDockEmployeeSidebar;
